import './Signup.scss'

import { useState, useEffect, useCallback } from 'react'
import { useLocation, Redirect } from 'react-router-dom'
import {
  Col,
  Container,
  Row,
  FloatingLabel,
  Form,
  Button,
  Spinner,
  Alert
} from 'react-bootstrap'
import * as Sentry from '@sentry/react'

import RembleLogomark from '../../../assets/images/Remble_Logomark-Green.png'
import { ReactComponent as AppStoreButton } from '../../../assets/images/app_store_button.svg'
import PlayStoreButton from '../../../assets/images/google-play-badge.png'
import AppleSignInButton from '../../../assets/images/btn_apple_signin.png'
import AppleSignUpButton from '../../../assets/images/btn_apple_signup.png'
import GoogleSignInButton from '../../../assets/images/btn_google_signin.png'
import GoogleSignUpButton from '../../../assets/images/btn_google_signup.png'
import OpenIDSignInButton from '../../../assets/images/btn_openid_signin.png'
import OpenIDSignUpButton from '../../../assets/images/btn_openid_signup.png'

import {
  createUser,
  verifyUsernameIsUnique,
  authenticateUser
} from 'components/common/api/Users'
import {
  EVENT_ACTION_LOGIN,
  EVENT_CATEGORY_ACCOUNT,
  saveUserEvent,
  trackGAEvent,
  trackMetaEvent
} from 'components/common/api/UserEvents'
import { isEmail } from 'components/common/helpers/Strings'
import {
  getAppleLoginUrl,
  getAuthenticatedUser,
  getGoogleLoginUrl,
  login,
  REMBLE_OAUTH_ID_TOKEN_KEY,
  REMBLE_OAUTH_STATE_KEY,
  setLocalAuthKey,
  storeVerificationData
} from 'components/auth'
import {
  postMobileKeychain,
  postMobileToken,
  isInApp,
  openUrlInSafari,
  toggleTabBar,
  requestNotificationAuthorization,
  isiOS,
  mobileOAuth,
  isAndroid
} from 'components/common/helpers/Mobile'
import { navigateTo } from 'components/common/navigation'
import ComplianceFooter from 'components/common/navigation/ComplianceFooter'
import {
  ERR_USERNAME_ALREADY_REGISTERED,
  ERR_USER_NOT_CONFIRMED
} from 'components/common/api'
import {
  ROUTE_VERIFY,
  ROUTE_TODAY,
  ROUTE_LOGIN_OAUTH,
  ROUTE_LOGIN_OAUTH_OPENID
} from 'components/common/helpers/Paths'
import { storeAttributionParams } from 'components/common/helpers/Tracking'
import PasswordGroup from '../PasswordGroup'
import { KEY_SIGNUP_CODE } from 'components/common/api/Payments'

/**
 * Unified Login/Signup flow
 */
export default function Signup({
  history,
  setIsAuthenticated,
  defaultToNewUser = true
}) {
  // Utility
  const user = getAuthenticatedUser()
  const { search } = useLocation()
  const [loading, setLoading] = useState(false)
  const [isNewUser, setIsNewUser] = useState(defaultToNewUser)
  const [didUserCheck, setDidUserCheck] = useState(false)
  const [signupError, setSignupError] = useState()

  // Did we redirect with a return path from an auth-required page?
  const searchParams = new URLSearchParams(search)
  const returnPath = searchParams.get('returnPath') || ROUTE_TODAY
  const wasRedirected = returnPath !== ROUTE_TODAY

  // Did we pass a base64-encoded email address in the URL?
  let encodedEmail = searchParams.get('e')
  try {
    encodedEmail = atob(encodedEmail)
  } catch (e) {} // Silently fail if the email is invalid

  // Did we pass a signup code in the URL?
  const signupCode = searchParams.get(KEY_SIGNUP_CODE)

  // Social login
  const [shouldShowSocialLogin, setShouldShowSocialLogin] = useState(false)

  // Email
  const [email, setEmail] = useState(
    encodedEmail && encodedEmail.includes('@') ? encodedEmail : ''
  )
  const [isValidEmail, setIsValidEmail] = useState(true) // Default to true, since we autofocus

  // Password and password confirmation (as returned from PasswordGroup)
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isValidPasswordCombo, setIsValidPasswordCombo] = useState(false)

  // Name
  const [name, setName] = useState('')
  const [isValidName, setIsValidName] = useState(true)

  // Terms of Use
  const [termsOfUse, setTermsOfUse] = useState()
  const [isValidTermsOfUse, setIsValidTermsOfUse] = useState(true)

  // Validity check
  const isValidSignup =
    isValidEmail &&
    isValidPasswordCombo &&
    !!name.length &&
    isValidName &&
    !!termsOfUse // Check for terms, not valid state

  /**
   * Helper function to see if the `email` provided is an existing user
   * @param {String} value The field's value
   */
  const existingUserCheck = async event => {
    // Return early if it's not a valid email
    if (!email || !isValidEmail) return

    // Let's verify that the username isn't already registered before we get too far
    setLoading(true)

    // Check for response type
    let isUnique = await verifyUsernameIsUnique(email)

    // Error communicating with the backend server
    // (e.g. network error, or login server is down or not responding)
    if (isUnique.error) return setSignupError(isUnique.error)

    setLoading(false)
    setIsNewUser(isUnique.valid) // Disable?
    setDidUserCheck(true)

    // Show and focus the password field
    document.getElementById('user-password')?.focus()
  }

  /**
   * Sets and runs validation on `email` field
   * @param {String} value The field's value
   */
  const setAndValidateEmail = value => {
    const isValid = isEmail(value)

    setEmail(value)
    setIsValidEmail(isValid)
  }

  /**
   * Sets and runs validation on TOS field
   * @param {String} value The field's value
   */
  const setAndValidateTermsAcceptance = value => {
    const isValid = value

    setTermsOfUse(value)
    setIsValidTermsOfUse(isValid)
  }

  /**
   * Sets and runs validation on `name` field
   * @param {String} value The field's value
   */
  const setAndValidateName = value => {
    value = value.trim() // Trim it
    const isValid = !!value.length // If our string has any non-empty value, it's valid

    setName(value)
    setIsValidName(isValid)
  }

  /**
   * Attempts a login with the passed in `email` and `password`
   * @param {String} email The user's email
   * @param {String} password The user's password
   */
  const attemptLogin = async (email, password) => {
    setLoading(true)
    // Wait for authentication
    await authenticateUser({
      email,
      password
    })
      .then(async result => {
        // Success
        if (result.token) return await handleAuthSuccess(result, returnPath)

        // Exception
        return await handleExceptions(result.detail)
      })
      .catch(error => handleExceptions(error))
  }

  /**
   * Attempts a login via the bridge with the passed in `email` and `password`
   * @param {String} email The user's email
   * @param {String} password The user's password
   */
  const attemptBridgeLogin = useCallback(
    async (email, password) => {
      // Wait for authentication
      await authenticateUser({
        email,
        password
      })
        .then(async result => {
          // Success?
          if (!result.token) return

          // Get the user data from the auth token
          const authenticatedUser = getAuthenticatedUser(result.token)
          const remble_id = authenticatedUser.aurora_id

          setIsAuthenticated(true)
          postMobileKeychain({ email, password, remble_id })
          login(result)
          postMobileToken(result.token)
          setLocalAuthKey(result.token) // Sets local storage to dismiss the in-app auth screen
          navigateTo(returnPath, history)
        })
        .catch(error => console.error(error))
    },
    [setIsAuthenticated, history, returnPath]
  )

  /**
   * Helper function to handle clicks on privacy policy link
   * @param {Event} event The click event
   * @param {String} url The URL to open
   */
  const handlePrivacyPolicyClick = (event, url) => {
    // If we are in the app and the user clicks on the privacy policy link, let's open the link in a native window
    // TODO: Handle for Android
    if (isInApp()) {
      event.preventDefault()
      event.stopPropagation()

      openUrlInSafari(url)
    }
  }

  /**
   * Helper function to handle clicks on terms of use link
   * @param {Event} event The click event
   */
  const handleTermsOfUseClick = (event, url) => {
    // If we are in the app and the user clicks on the privacy policy link, let's open the link in a native window
    // TODO: Handle for Android
    if (isInApp()) {
      event.preventDefault()
      event.stopPropagation()

      openUrlInSafari(url)
    }
  }

  /**
   * Helper function to handle various exception cases
   * @param {String} detail Error detail as passed through via the API
   */
  const handleExceptions = async detail => {
    // Handle exception cases
    switch (detail) {
      // Did we accidentally get stuck in the signup flow?
      // Let's attempt a login again
      case ERR_USERNAME_ALREADY_REGISTERED:
        return await attemptLogin(email, password)

      // User has not completed account verification
      case ERR_USER_NOT_CONFIRMED:
        storeVerificationData({ email, password })
        return navigateTo(ROUTE_VERIFY, history)

      // Uncaught or other exception
      default:
        // If user attempted login, let's refocus the password field
        if (!isNewUser) document.getElementById('user-password')?.focus()
        setLoading(false)
        return setSignupError(detail)
    }
  }

  const registerOAuthEventListeners = provider => {
    // Listen for the `oauth` events
    // Add listeners to handle the response from the native in-app purchase flow
    window.addEventListener('oauthSuccess', event => {
      // Get the id token from local storage
      const idToken = localStorage.getItem(REMBLE_OAUTH_ID_TOKEN_KEY)

      // If we have an id token, let's redirect to the oauth page
      if (idToken) {
        // Build the URL
        const url = `${ROUTE_LOGIN_OAUTH}?state=native&code=native&provider=${provider}`
        navigateTo(url, history)
      } else {
        setSignupError(
          'There was an error logging in. Please try again or contact Remble Support (support@remble.com).'
        )
      }
    })

    window.addEventListener('oauthCancel', event => {
      setLoading(false)
    })

    window.addEventListener('oauthError', event => {
      setSignupError(
        'There was an error logging in. Please try again or contact Remble Support (support@remble.com).'
      )
      setLoading(false)
    })
  }

  /**
   * Helper function to handle clicks on the Apple login button
   * @param {Event} event The click event
   */
  const handleAppleButtonClick = async event => {
    event.preventDefault()
    event.stopPropagation()

    // Set loading state and clear any previous errors
    setLoading(true)
    setSignupError(null)

    // If we are in the app, let's use the native Apple login
    if (isInApp()) {
      registerOAuthEventListeners('apple')
      mobileOAuth('apple')
      return
    }

    try {
      // Otherwise, let's use the web flow
      // Fetch the Apple login URL
      const { url } = await getAppleLoginUrl()

      // Pull out the state from the URL
      let state = url.split('state=')[1]

      // Check for an ampersand and remove it if it exists
      if (state.includes('&')) state = state.split('&')[0]

      // Save the state to local storage
      localStorage.setItem(REMBLE_OAUTH_STATE_KEY, state)

      // If we have a signup code, let's save it to local storage for later
      if (signupCode) localStorage.setItem(KEY_SIGNUP_CODE, signupCode)

      // Redirect to the Apple login URL
      if (url) window.location.href = url
    } catch (error) {
      console.error(error)
      setSignupError(
        "We're sorry, but we were unable to log you in. Please try again later or use your email to sign in."
      )
      setLoading(false)
    }
  }

  /**
   * Helper function to handle clicks on the Google login button
   * @param {Event} event The click event
   */
  const handleGoogleButtonClick = async event => {
    event.preventDefault()
    event.stopPropagation()

    // Set loading state and clear any previous errors
    setLoading(true)
    setSignupError(null)

    // If we are in the app, let's use the native Google login
    if (isInApp()) {
      registerOAuthEventListeners('google')
      mobileOAuth('google')
      return
    }

    try {
      // Otherwise, let's use the web flow
      // Fetch the Google login URL
      const { url } = await getGoogleLoginUrl()

      // Pull out the state from the URL
      let state = url.split('state=')[1]

      // Check for an ampersand and remove it if it exists
      if (state.includes('&')) state = state.split('&')[0]

      // Save the state to local storage
      localStorage.setItem(REMBLE_OAUTH_STATE_KEY, state)

      // If we have a signup code, let's save it to local storage for later
      if (signupCode) localStorage.setItem(KEY_SIGNUP_CODE, signupCode)

      // Redirect to the Google login URL
      if (url) window.location.href = url
    } catch (error) {
      console.error(error)
      setSignupError(
        "We're sorry, but we were unable to log you in. Please try again later or use your email to sign in."
      )
      setLoading(false)
    }
  }

  /**
   * Helper function to handle clicks on the OpenID login button
   * @param {Event} event The click event
   */
  const handleOpenIDButtonClick = async event => {
    event.preventDefault()
    event.stopPropagation()

    return navigateTo(ROUTE_LOGIN_OAUTH_OPENID, history)
  }

  /**
   * Helper function to handle successful authentication
   * @param {Object} data JWT Authentication data
   * @param {String} redirectURL The URL to redirect users to
   */
  const handleAuthSuccess = async (data, redirectURL) => {
    login(data)
    setIsAuthenticated(true)

    // Log access
    await saveUserEvent(EVENT_CATEGORY_ACCOUNT, EVENT_ACTION_LOGIN)

    // Successful login? Save credentials to keychain
    if (isInApp() && redirectURL === ROUTE_TODAY) {
      // Re-request notification authorization, in case the user has not been prompted yet
      requestNotificationAuthorization()

      // Get the user data from the auth token
      const authenticatedUser = getAuthenticatedUser(data.token)
      const remble_id = authenticatedUser.aurora_id

      postMobileKeychain({ email, password, remble_id }) // Store credentials in iOS keychain
      postMobileToken(data.token) // Store token for app and dismiss AuthViewController in old app
      setLocalAuthKey(data.token) // Sets local storage to dismiss the in-app auth screen
    }
    return navigateTo(redirectURL, history)
  }

  /**
   * Form submission handler for the completed form flow
   * @param {Event} event
   */
  const handleSubmit = async event => {
    // Prevent event bubbling
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Something is wrong, we can't proceed
    if (isNewUser && !isValidSignup) return

    // Looks good, let's clear any errors and submit the form
    setSignupError(null)
    setLoading(true)

    // Store the attribution params
    storeAttributionParams(search)

    // Exising user? Let's attempt a login
    if (!isNewUser) return await attemptLogin(email, password)

    // New user? Let's attempt a signup. Don't forget to pass along the signup data
    // Pull all query params from the URL and add them to the signup data object array
    let signupData = []

    for (const [key, value] of searchParams.entries()) {
      signupData.push({ [key]: value })
    }

    // If we have a signup code, let's save it to local storage for later
    if (signupCode) localStorage.setItem(KEY_SIGNUP_CODE, signupCode)

    // New user
    await createUser({
      name,
      email,
      password,
      password_confirm: passwordConfirm,
      signup_data: signupData
    })
      .then(async result => {
        setLoading(false)
        storeVerificationData({ email, password })

        // Success
        if (result.token) {
          return await handleAuthSuccess(result, ROUTE_VERIFY)
        }

        // Exception
        return await handleExceptions(result.detail)
      })
      .catch(error => setSignupError(error))
  }

  /**
   * Main effect handler, attempts autologin for mobile app users
   */
  useEffect(() => {
    // Return early if we don't have the bridge login ready
    if (!attemptBridgeLogin) return

    // Track to Meta
    trackMetaEvent('ViewContent', { content_name: 'Viewed Signup' })

    // Track to GA
    trackGAEvent('page_view', 'signup', 'Viewed Signup')

    // Disable the tab bar on mobile
    toggleTabBar('off')

    // Check for social login support
    setShouldShowSocialLogin(isiOS('1.4.0') || isAndroid('1.4.0') || !isInApp())

    // TODO: Remove this once enough users have upgraded to v1.2 or higher
    // Deprecated in favor of using `PrivateLogin.jsx` and the private `/_login` route
    // Attempt to autologin the user, but fail gracefully
    try {
      const shouldLogin =
        window.bridgeStart &&
        window.bridgeAutoLogin &&
        !window.bridgeAutoLogin.attempted

      // Return early if we don't need to auto login
      if (!shouldLogin) return

      window.bridgeAutoLogin.attempted = true
      setIsNewUser(false)
      attemptBridgeLogin(
        window.bridgeAutoLogin.email,
        window.bridgeAutoLogin.password
      )
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
    }
  }, [attemptBridgeLogin])

  // Is the user already logged in?
  // If so, let's redirect them to the Today screen
  if (user && !didUserCheck) return <Redirect to={returnPath} />

  return (
    <Container fluid className='signup-container'>
      <Row className='text-center'>
        <Col>
          <img
            className='logomark'
            src={RembleLogomark}
            alt='Remble'
            width='80'
            fetchpriority='high'
          />
          <h1 className='mt-4 title'>Let’s Remble</h1>

          {/* If the user was redirected, let's show a notice */}
          {wasRedirected && (
            <Alert variant='light green'>
              You must be logged in to view that page
            </Alert>
          )}

          <Row>
            {/* Don't show Apple or Google login buttons unless we have support */}
            {shouldShowSocialLogin && (
              <>
                {/* Don't show apple login on Android */}
                {!isAndroid() && (
                  <Col xs={12} className='p-2'>
                    <a
                      href='/'
                      className='apple-signin'
                      onClick={handleAppleButtonClick}
                    >
                      <img
                        src={isNewUser ? AppleSignUpButton : AppleSignInButton}
                        alt='Sign in with Apple'
                        width='191'
                      />
                    </a>
                  </Col>
                )}
                <Col xs={12} className='p-2'>
                  <a
                    href='/'
                    className='google-signin'
                    onClick={handleGoogleButtonClick}
                  >
                    <img
                      src={isNewUser ? GoogleSignUpButton : GoogleSignInButton}
                      alt='Sign in with Google'
                      width='191'
                    />
                  </a>
                </Col>
              </>
            )}
            <Col xs={12} className='p-2'>
              <a
                href='/'
                className='openid-signin'
                onClick={handleOpenIDButtonClick}
              >
                <img
                  src={isNewUser ? OpenIDSignUpButton : OpenIDSignInButton}
                  alt='Sign in with OpenID'
                  width='191'
                />
              </a>
            </Col>
          </Row>
          <hr />

          {isNewUser ? (
            <>
              <h4 className='mt-4'>Sign up with email</h4>
              <p className='muted small'>
                Already have an account?{' '}
                <span
                  className='signup-link'
                  onClick={_ => {
                    // Return early if we're still loading
                    if (loading) return

                    setIsNewUser(false)
                    document.getElementById('user-email')?.focus()
                  }}
                >
                  Login here
                </span>
              </p>
            </>
          ) : (
            <>
              <h4 className='mt-4'>Log in with email</h4>
              <p className='muted small'>
                Don't have an account?{' '}
                <span
                  className='signup-link'
                  onClick={_ => {
                    // Return early if we're still loading
                    if (loading) return

                    setIsNewUser(true)
                    document.getElementById('user-email')?.focus()
                  }}
                >
                  Sign up here
                </span>
              </p>
            </>
          )}
        </Col>
      </Row>
      <Row>
        <Form className='signup-form' onSubmit={handleSubmit} noValidate>
          {/* Let's start with email */}
          <Form.Group controlId='formEmail'>
            <FloatingLabel label='Your email address'>
              <Form.Control
                id='user-email'
                name='email'
                disabled={loading}
                size='lg'
                placeholder='Your email address'
                required
                type='email'
                autoFocus={true}
                onChange={e => setAndValidateEmail(e.currentTarget.value)}
                onBlur={existingUserCheck}
                isInvalid={!isValidEmail}
                isValid={email && isValidEmail}
                defaultValue={email}
              />
              <Form.Control.Feedback type='invalid'>
                Please provide a valid email address
              </Form.Control.Feedback>
              <Form.Control.Feedback>
                {/* Show a contextual message, depending on user state */}
                {isNewUser
                  ? 'Thank you for trusting us with this. We’ll keep it safe.'
                  : didUserCheck
                  ? 'Welcome back! 🙂'
                  : ''}
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {/* Combined Password & Password Confirm component */}
          <PasswordGroup
            isNewUser={isNewUser}
            onValidStateChange={isValid => {
              setIsValidPasswordCombo(isValid)
            }}
            onPasswordsValid={isEnterKey => {
              // If our password confirmation looks good, let's show and focus the name field
              if (isEnterKey) document.getElementById('floatingName')?.focus()
            }}
            onSetPassword={password => {
              setPassword(password)
            }}
            onSetPasswordConfirm={passwordConfirm => {
              setPasswordConfirm(passwordConfirm)
            }}
            handleSubmit={handleSubmit}
            loading={loading}
            showForgotPassword={!isNewUser}
          />

          <Form.Group
            className={`form-group ${isNewUser ? 'visible' : ''}`}
            controlId='formName'
          >
            <FloatingLabel
              controlId='floatingName'
              label='What should we call you?'
            >
              <Form.Control
                name='name'
                disabled={loading}
                size='lg'
                placeholder='What should we call you?'
                required
                type='text'
                onChange={e => setAndValidateName(e.currentTarget.value)}
                isInvalid={!isValidName}
                isValid={name && isValidName}
              />
              <Form.Control.Feedback type='invalid'>
                Your name is required.
              </Form.Control.Feedback>
              <Form.Control.Feedback>
                Hi {name}, welcome to Remble 😊 Your name will help to
                personalize your experience
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>

          {/* Only show TOS acceptance checkbox after we have password confirmation */}
          <Form.Group
            className={`form-group ${isNewUser ? 'visible' : ''}`}
            controlId='formTermsOfUse'
          >
            {/* Wrap checks in this div to fix Safari rendering issue */}
            <div className='form-floating'>
              <Form.Check
                name='terms_acceptance'
                type='checkbox'
                id={`check-termsOfUse`}
              >
                <Form.Check.Input
                  type='checkbox'
                  required
                  onChange={e =>
                    setAndValidateTermsAcceptance(e.currentTarget.checked)
                  }
                  isInvalid={!isValidTermsOfUse}
                  isValid={termsOfUse && isValidTermsOfUse}
                />
                {'  '}I agree to the Remble{' '}
                <a
                  href='https://www.remble.com/terms-of-use'
                  target='_blank'
                  rel='noreferrer'
                  onClick={e =>
                    handleTermsOfUseClick(
                      e,
                      'https://www.remble.com/terms-of-use'
                    )
                  }
                >
                  Terms of Use
                </a>{' '}
                and the{' '}
                <a
                  href='https://www.remble.com/privacy'
                  target='_blank'
                  rel='noreferrer'
                  onClick={e =>
                    handlePrivacyPolicyClick(
                      e,
                      'https://www.remble.com/privacy'
                    )
                  }
                >
                  Privacy Policy
                </a>
                <Form.Control.Feedback type='valid'>
                  Thank you!
                </Form.Control.Feedback>
                <Form.Control.Feedback type='invalid'>
                  You must accept the Terms of Use in order to use Remble.
                </Form.Control.Feedback>
              </Form.Check>
            </div>
          </Form.Group>

          {/* Did we receive a signup error? */}
          {signupError && (
            <div className='error'>
              <i className='icon bi-exclamation-circle'></i>{' '}
              <strong>Error: </strong>
              {/* Do we have an array of messages or a string? */}
              {!Array.isArray(signupError) ? (
                String(signupError) // Force to string
              ) : (
                <>
                  {signupError.map((error, i) => (
                    <span key={i}>{error.msg} </span>
                  ))}
                </>
              )}
            </div>
          )}

          <Button
            variant='primary'
            size='lg'
            type='submit'
            disabled={loading || (isNewUser && !isValidSignup)}
            className='w-100 mt-5'
          >
            {loading ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                />
                <span className='px-2'>Loading...</span>
              </>
            ) : (
              <span>{isNewUser ? 'Get Started!' : 'Log in'}</span>
            )}
          </Button>
        </Form>
      </Row>
      <ComplianceFooter navigateWithin={false} />

      {/* Don't show app store button within the mobile app */}
      {!isInApp() && (
        <div className='app-store-button-wrapper'>
          <a
            href='https://apps.apple.com/us/app/remble-relationship-health/id1597957847'
            target='_blank'
            rel='noreferrer'
            onClick={e => {
              e.preventDefault()

              // Track to Meta
              trackMetaEvent('Lead', {
                content_name: 'Clicked App Store Button'
              })

              // Track to GA
              trackGAEvent('lead', 'lead', 'Clicked App Store Button')

              window.open(e.currentTarget.href, '_blank')
            }}
          >
            <AppStoreButton />
          </a>{' '}
          <a
            href='https://play.google.com/store/apps/details?id=com.remble.android'
            target='_blank'
            rel='noreferrer'
            onClick={e => {
              e.preventDefault()

              // Track to Meta
              trackMetaEvent('Lead', {
                content_name: 'Clicked Play Store Button'
              })

              // Track to GA
              trackGAEvent('lead', 'lead', 'Clicked Play Store Button')

              window.open(e.currentTarget.href, '_blank')
            }}
          >
            <img
              className='play-store-button'
              src={PlayStoreButton}
              alt='Get it on Google Play'
              width='160'
            />
          </a>
        </div>
      )}
    </Container>
  )
}
